import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Paper,
  Typography,
  Grid,
  Chip,
  Box,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
} from '@mui/material';
import {
} from '@mui/icons-material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import LocalCafeIcon from '@mui/icons-material/LocalCafe';
import CategoryIcon from '@mui/icons-material/Category';
import LocalDiningIcon from '@mui/icons-material/LocalDining';
import TimerIcon from '@mui/icons-material/Timer';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import DescriptionIcon from '@mui/icons-material/Description';
import DateRangeIcon from '@mui/icons-material/DateRange';
import PersonIcon from '@mui/icons-material/Person';
import EditIcon from '@mui/icons-material/Edit';
import { useAuth } from "../../../../Context/AuthContext";
import { getUsersAccountsList } from "../../../../services/Admin/UsersAccountsApi";


const DrinkDetailsDialog = ({ open, onClose, drink, onEdit }) => {
  const [ingredients, setIngredients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [usersMap, setUsersMap] = useState({});
  const [usersLoading, setUsersLoading] = useState(false);
  const { hasPermission } = useAuth();

  useEffect(() => {
    if (open && drink) {
      setLoading(true);
      // محاولة تحليل المكونات من JSON أو استخدام materials مباشرة
      try {
        if (drink.materials && Array.isArray(drink.materials) && drink.materials.length > 0) {
          setIngredients(drink.materials);
        } else if (drink.ingredients && typeof drink.ingredients === 'string') {
          const parsed = JSON.parse(drink.ingredients);
          setIngredients(parsed);
        } else {
          setIngredients([]);
        }
      } catch (error) {
        console.error('خطأ في تحليل المكونات:', error);
        setIngredients([]);
      }
      setLoading(false);
    }
  }, [open, drink]);

  useEffect(() => {
    let mounted = true;

    const fetchUsers = async () => {
      if (!open) return;

      setUsersLoading(true);
      try {
        const response = await getUsersAccountsList();
        const users = Array.isArray(response?.data) ? response.data : [];
        const map = users.reduce((acc, user) => {
          if (user?.id !== undefined && user?.id !== null) {
            acc[String(user.id)] = user;
          }
          return acc;
        }, {});

        if (mounted) setUsersMap(map);
      } catch (error) {
        console.error('Failed to load users accounts:', error);
        if (mounted) setUsersMap({});
      } finally {
        if (mounted) setUsersLoading(false);
      }
    };

    fetchUsers();
    return () => {
      mounted = false;
    };
  }, [open]);

  const getApprovalChip = () => {
    if (drink.is_approved === 1) {
      return (
        <Chip
          icon={<CheckCircleIcon />}
          label="معتمد"
          color="success"
          variant="filled"
          sx={{ fontWeight: 'bold' }}
        />
      );
    }
    if (drink.is_approved === 0) {
      return (
        <Chip
          icon={<HourglassTopIcon />}
          label="قيد المراجعة"
          color="warning"
          variant="filled"
          sx={{ fontWeight: 'bold' }}
        />
      );
    }
    return (
      <Chip
        icon={<CancelIcon />}
        label="غير معتمد"
        color="error"
        variant="filled"
        sx={{ fontWeight: 'bold' }}
      />
    );
  };

  const getStatusChip = () => {
    return drink.status === 1 ? (
      <Chip label="نشط" color="success" size="small" />
    ) : (
      <Chip label="غير نشط" color="default" size="small" />
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('ar-SA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const getCreatorId = () =>
    drink?.created_by ||
    drink?.created_by_id ||
    drink?.creator_id ||
    drink?.user_id ||
    drink?.creator?.id ||
    null;

  const getCreatorInfo = () => {
    if (drink?.creator && typeof drink.creator === 'object') {
      return drink.creator;
    }

    const creatorId = getCreatorId();
    if (creatorId === null || creatorId === undefined || creatorId === '') {
      return null;
    }

    return usersMap[String(creatorId)] || null;
  };

  if (!drink) return null;

  const creatorInfo = getCreatorInfo();
  const creatorId = getCreatorId();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', textAlign: 'right', fontWeight: 'bold' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}>
          <LocalCafeIcon />
          تفاصيل المشروب
        </Box>
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={3} dir="rtl">
            {/* معلومات أساسية */}
            <Grid item xs={12}>
              <Paper sx={{ p: 2, bgcolor: 'background.paper', border: '1px solid #e0e0e0' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    المعلومات الأساسية
                  </Typography>
                  <Avatar sx={{ bgcolor: 'primary.main' }}>
                    <LocalCafeIcon />
                  </Avatar>
                </Box>

                <Grid container spacing={2}>
                  {/* الرقم */}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="textSecondary">
                      الرقم التعريفي
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {drink.id}
                    </Typography>
                  </Grid>

                  {/* اسم المشروب */}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="textSecondary">
                      اسم المشروب
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {drink.drink_name || '-'}
                    </Typography>
                  </Grid>

                  {/* الاسم بالعربية */}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="textSecondary">
                      الاسم (عربي)
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {drink.name || '-'}
                    </Typography>
                  </Grid>

                  {/* الاسم بالإنجليزية */}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="textSecondary">
                      الاسم (English)
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {drink.name_en || '-'}
                    </Typography>
                  </Grid>

                  {/* الفئة */}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="textSecondary">
                      الفئة
                    </Typography>
                    {drink.category ? (
                      <Chip
                        icon={<CategoryIcon />}
                        label={drink.category.name || '-'}
                        color="primary"
                        variant="outlined"
                        size="small"
                      />
                    ) : (
                      <Typography variant="body2">-</Typography>
                    )}
                  </Grid>

                  {/* حالة الموافقة */}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="textSecondary">
                      حالة الموافقة
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      {getApprovalChip()}
                    </Box>
                  </Grid>

                  {/* الحالة */}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="textSecondary">
                      الحالة
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      {getStatusChip()}
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* الوصف */}
            <Grid item xs={12}>
              <Paper sx={{ p: 2, bgcolor: 'background.paper', border: '1px solid #e0e0e0' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <DescriptionIcon color="primary" />
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    الوصف
                  </Typography>
                </Box>

                <Grid container spacing={2}>
                  {/* الوصف بالعربية */}
                  <Grid item xs={12}>
                    <Typography variant="caption" color="textSecondary">
                      الوصف (عربي)
                    </Typography>
                    <Paper sx={{ p: 1, bgcolor: '#f5f5f5', mt: 0.5 }}>
                      <Typography variant="body2">
                        {drink.description || '-'}
                      </Typography>
                    </Paper>
                  </Grid>

                  {/* الوصف بالإنجليزية */}
                  <Grid item xs={12}>
                    <Typography variant="caption" color="textSecondary">
                      الوصف (English)
                    </Typography>
                    <Paper sx={{ p: 1, bgcolor: '#f5f5f5', mt: 0.5 }}>
                      <Typography variant="body2">
                        {drink.description_en || '-'}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>

                {/* ملاحظات الموافقة */}
                {drink.approval_notes && (
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="textSecondary">
                        ملاحظات الموافقة
                      </Typography>
                      <Paper sx={{ p: 1, bgcolor: '#fff3cd', mt: 0.5, borderLeft: '4px solid #ff9800' }}>
                        <Typography variant="body2">
                          {drink.approval_notes}
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                )}
              </Paper>
            </Grid>

            {/* معلومات التحضير */}
            <Grid item xs={12}>
              <Paper sx={{ p: 2, bgcolor: 'background.paper', border: '1px solid #e0e0e0' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <LocalDiningIcon color="primary" />
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    معلومات التحضير والسعر
                  </Typography>
                </Box>

                <Grid container spacing={2}>
                  {/* درجة الحرارة */}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="textSecondary">
                      درجة الحرارة
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {drink.temperature || '-'}
                    </Typography>
                  </Grid>

                  {/* وقت التحضير */}
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TimerIcon fontSize="small" color="primary" />
                      <Box>
                        <Typography variant="caption" color="textSecondary">
                          وقت التحضير (دقيقة)
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {drink.prepare_time || '-'}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>

                  {/* السعر */}
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AttachMoneyIcon fontSize="small" color="success" />
                      <Box>
                        <Typography variant="caption" color="textSecondary">
                          السعر
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                          {drink.price || '-'}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>

  
                </Grid>
              </Paper>
            </Grid>

            {/* المكونات */}
            <Grid item xs={12}>
              <Paper sx={{ p: 2, bgcolor: 'background.paper', border: '1px solid #e0e0e0' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <LocalCafeIcon color="primary" />
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    المكونات
                  </Typography>
                </Box>

                {ingredients && ingredients.length > 0 ? (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'primary.light' }}>
                          <TableCell sx={{ textAlign: 'right', color: 'white', fontWeight: 'bold' }}>
                            المكون
                          </TableCell>
                          <TableCell sx={{ textAlign: 'right', color: 'white', fontWeight: 'bold' }}>
                            الكمية
                          </TableCell>
                          <TableCell sx={{ textAlign: 'right', color: 'white', fontWeight: 'bold' }}>
                            الوحدة
                          </TableCell>
                          <TableCell sx={{ textAlign: 'right', color: 'white', fontWeight: 'bold' }}>
                            التكلفة
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {ingredients.map((ingredient, index) => {
                          // التعامل مع تنسيقات مختلفة للمكونات
                          const materialName =
                            ingredient.material?.name ||
                            ingredient.name ||
                            ingredient.material_name ||
                            '-';
                          const quantity =
                            ingredient.quantity ||
                            ingredient.amount ||
                            '-';
                          const unit =
                            ingredient.material?.measurment_unit?.abbreviation ||
                            ingredient.unit ||
                            '-';
                          const costPrice =
                            ingredient.material?.cost_price_per_unit ||
                            '-';

                          return (
                            <TableRow
                              key={index}
                              sx={{
                                '&:hover': { bgcolor: '#f5f5f5' },
                                borderBottom: '1px solid #e0e0e0',
                              }}
                            >
                              <TableCell sx={{ textAlign: 'right' }}>
                                <Typography variant="body2">
                                  {materialName}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ textAlign: 'right' }}>
                                <Chip
                                  label={quantity}
                                  size="small"
                                  variant="outlined"
                                  color="primary"
                                />
                              </TableCell>
                              <TableCell sx={{ textAlign: 'right' }}>
                                <Typography variant="caption" color="textSecondary">
                                  {unit}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ textAlign: 'right' }}>
                                <Typography variant="caption" color="success.main" sx={{ fontWeight: 'bold' }}>
                                  {costPrice}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Paper sx={{ p: 2, bgcolor: '#f5f5f5', textAlign: 'center' }}>
                    <Typography variant="body2" color="textSecondary">
                      لا توجد مكونات
                    </Typography>
                  </Paper>
                )}
              </Paper>
            </Grid>

            {/* معلومات التاريخ */}
            <Grid item xs={12}>
              <Paper sx={{ p: 2, bgcolor: 'background.paper', border: '1px solid #e0e0e0' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <DateRangeIcon color="primary" />
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    معلومات التاريخ
                  </Typography>
                </Box>

                <Grid container spacing={2}>
                  {/* تاريخ الإنشاء */}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="textSecondary">
                      تاريخ الإنشاء
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {formatDate(drink.created_at)}
                    </Typography>
                  </Grid>
                  {/* من قام بالإنشاء */}
                  {creatorId && (
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon fontSize="small" color="primary" />
                        <Box>
                          <Typography variant="caption" color="textSecondary">
                            من قام بالإنشاء
                          </Typography>
                          {usersLoading ? (
                            <Typography variant="body2" color="text.secondary">
                              جاري تحميل بيانات العميل...
                            </Typography>
                          ) : creatorInfo ? (
                            <>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {creatorInfo.name || '-'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {creatorInfo.phone || '-'}
                              </Typography>
                            </>
                          ) : (
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              {creatorId}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, justifyContent: 'flex-start', gap: 1 }}>
        {hasPermission("edit") && (
        <Button
          onClick={() => onEdit?.(drink)}
          variant="contained"
          color="warning"
          startIcon={<EditIcon />}
        >
          تعديل
        </Button>
        )}
        <Button onClick={onClose} variant="contained" color="primary">
          إغلاق
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DrinkDetailsDialog;
